import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'
import { SCHEDULE_RATE_UNIT } from '@trustero/trustero-api-web/lib/schedulemanager/schedulemanager_pb'
import { AUDIT_SCAN_FREQUENCY_STATUS } from 'src/components/Reusable/StatusIndicator/StatusIndicator.constants'

export type AccountSettingsConfigType = {
  title: string
  button: JSX.Element
}

export type DateLimitValue = {
  title: DateLimitOptions
  dateLabel: string
  dateValue: Timestamp | null
  isRecommended: boolean
}

export type AuditScanFrequencyConfigType = {
  label: AuditScanFrequencyLabels
  seconds: number
  status: AUDIT_SCAN_FREQUENCY_STATUS
  rateValue: number | null
  rateUnit?: SCHEDULE_RATE_UNIT
  description?: string
  isRecommended?: boolean
}

export type DateLimitRecommendations = {
  initialValue: DateLimitOptions
  recommendations: DateLimitConfigType
}

export type DateLimitConfigType = {
  [key in DateLimitOptions]: DateLimitValue | null
}

export enum AccountSections {
  DATE_LIMIT = 'Date limit',
  IMAGE_TIMESTAMP_VALIDATION = 'Image timestamp validation',
  AUDIT_SCAN_FREQUENCY = 'AI Examination and Test Frequency',
  GOOGLE_DRIVE = 'Google Drive',
  ANECDOTES = 'Anecdotes',
  SHAREPOINT = 'SharePoint',
  CONFLUENCE = 'Confluence',
  CONFIGURE_RISK_CATEGORY = 'Manage risk categories',
  CONFIGURE_DEPARTMENTS = 'Manage departments',
  JIRA_WORKFLOW = 'Jira Workflow',
  SNOW_WORKFLOW = 'ServiceNow Workflow',
}

export enum DateLimitOptions {
  ACCOUNT_CREATION = 'Account Creation',
  EARLIEST_OPEN_AUDIT = 'Start of Earliest Open Audit',
  CURRENT_BOUNDARY = 'Current Boundary',
  SIX_MONTHS_AGO = 'Six Months Ago Today',
}

export const CUSTOM_DATE_LIMIT_OPTION = 'Custom' as DateLimitOptions

export enum AuditScanFrequencyOptions {
  MANUAL_ONLY = 0,
  EVERY_FOUR_HOURS = 1,
  EVERY_TWELVE_HOURS = 2,
  DAILY = 3,
  WEEKLY = 4,
  BIWEEKLY = 5,
  MONTHLY = 6,
}

export enum AuditScanFrequencyLabels {
  MANUAL_ONLY = 'Manual only, no automatic checks. Receptors pull evidence weekly.',
  EVERY_FOUR_HOURS = 'Every 4 hours',
  EVERY_TWELVE_HOURS = 'Every 12 hours',
  DAILY = 'Every day',
  WEEKLY = 'Every week',
  BIWEEKLY = 'Every other week',
  MONTHLY = 'Every month',
}

export const CUSTOM_DATE_LIMIT_VALUE = {
  title: CUSTOM_DATE_LIMIT_OPTION,
  dateLabel: 'Select a date',
  dateValue: null,
  isRecommended: false,
}

export const INITIAL_DATE_LIMIT_CONFIG: DateLimitConfigType = {
  [DateLimitOptions.ACCOUNT_CREATION]: null,
  [DateLimitOptions.EARLIEST_OPEN_AUDIT]: null,
  [DateLimitOptions.CURRENT_BOUNDARY]: null,
  [DateLimitOptions.SIX_MONTHS_AGO]: null,
}

export const DATE_LIMIT_ERROR_MESSAGE =
  'Sorry, there was an error setting the date limit. Please try again.'

export const AUDIT_SCAN_FREQUENCY_ERROR_MESSAGE =
  'Sorry, there was an error updating your AI Examination and Test frequency. Please try again.'

export const VALIDATE_IMAGE_TIMESTAMP_ERROR_MESSAGE =
  'Sorry, there was an error setting the image validation. Please try again.'
export const DATE_LIMIT_UPDATE_MESSAGE = 'updated date limit'

export const DATE_LIMIT_BANNER_MESSAGE =
  'The date limit was updated recently. This affects what you see in the Compliance Roadmap.'

export const DATE_LIMIT_DESCRIPTION =
  'Changing this date will affect which evidence Trustero AI considers'

export const AUDIT_SCAN_FREQUENCY_DESCRIPTION =
  'How often should we examine and test your controls for compliance?'

export const AUDIT_SCAN_FREQUENCY_OPTION_TO_LABEL: Record<
  AuditScanFrequencyOptions,
  AuditScanFrequencyLabels
> = {
  [AuditScanFrequencyOptions.MANUAL_ONLY]: AuditScanFrequencyLabels.MANUAL_ONLY,
  [AuditScanFrequencyOptions.EVERY_FOUR_HOURS]:
    AuditScanFrequencyLabels.EVERY_FOUR_HOURS,
  [AuditScanFrequencyOptions.EVERY_TWELVE_HOURS]:
    AuditScanFrequencyLabels.EVERY_TWELVE_HOURS,
  [AuditScanFrequencyOptions.DAILY]: AuditScanFrequencyLabels.DAILY,
  [AuditScanFrequencyOptions.WEEKLY]: AuditScanFrequencyLabels.WEEKLY,
  [AuditScanFrequencyOptions.BIWEEKLY]: AuditScanFrequencyLabels.BIWEEKLY,
  [AuditScanFrequencyOptions.MONTHLY]: AuditScanFrequencyLabels.MONTHLY,
}

export const AUDIT_SCAN_LABEL_TO_OPTION: Record<
  AuditScanFrequencyLabels,
  AuditScanFrequencyOptions
> = {
  [AuditScanFrequencyLabels.MANUAL_ONLY]: AuditScanFrequencyOptions.MANUAL_ONLY,
  [AuditScanFrequencyLabels.EVERY_FOUR_HOURS]:
    AuditScanFrequencyOptions.EVERY_FOUR_HOURS,
  [AuditScanFrequencyLabels.EVERY_TWELVE_HOURS]:
    AuditScanFrequencyOptions.EVERY_TWELVE_HOURS,
  [AuditScanFrequencyLabels.DAILY]: AuditScanFrequencyOptions.DAILY,
  [AuditScanFrequencyLabels.WEEKLY]: AuditScanFrequencyOptions.WEEKLY,
  [AuditScanFrequencyLabels.BIWEEKLY]: AuditScanFrequencyOptions.BIWEEKLY,
  [AuditScanFrequencyLabels.MONTHLY]: AuditScanFrequencyOptions.MONTHLY,
}

const AUDIT_SCAN_FREQUENCY_CONFIG_MAP: Record<
  AuditScanFrequencyOptions,
  AuditScanFrequencyConfigType
> = {
  [AuditScanFrequencyOptions.MANUAL_ONLY]: {
    label: AuditScanFrequencyLabels.MANUAL_ONLY,
    seconds: 0,
    status: AUDIT_SCAN_FREQUENCY_STATUS.NEUTRAL,
    rateValue: null,
  },
  [AuditScanFrequencyOptions.EVERY_FOUR_HOURS]: {
    label: AuditScanFrequencyLabels.EVERY_FOUR_HOURS,
    seconds: 4 * 60 * 60,
    status: AUDIT_SCAN_FREQUENCY_STATUS.BETTER,
    description: 'Most expensive',
    rateValue: 4,
    rateUnit: SCHEDULE_RATE_UNIT.SCHEDULE_RATE_UNIT_HOURS,
  },
  [AuditScanFrequencyOptions.EVERY_TWELVE_HOURS]: {
    label: AuditScanFrequencyLabels.EVERY_TWELVE_HOURS,
    seconds: 12 * 60 * 60,
    status: AUDIT_SCAN_FREQUENCY_STATUS.BETTER,
    rateValue: 12,
    rateUnit: SCHEDULE_RATE_UNIT.SCHEDULE_RATE_UNIT_HOURS,
  },
  [AuditScanFrequencyOptions.DAILY]: {
    label: AuditScanFrequencyLabels.DAILY,
    seconds: 24 * 60 * 60,
    status: AUDIT_SCAN_FREQUENCY_STATUS.RECOMMENDED,
    rateValue: 1,
    rateUnit: SCHEDULE_RATE_UNIT.SCHEDULE_RATE_UNIT_DAYS,
  },
  [AuditScanFrequencyOptions.WEEKLY]: {
    label: AuditScanFrequencyLabels.WEEKLY,
    seconds: 7 * 24 * 60 * 60,
    status: AUDIT_SCAN_FREQUENCY_STATUS.RECOMMENDED,
    rateValue: 7,
    rateUnit: SCHEDULE_RATE_UNIT.SCHEDULE_RATE_UNIT_DAYS,
  },
  [AuditScanFrequencyOptions.BIWEEKLY]: {
    label: AuditScanFrequencyLabels.BIWEEKLY,
    seconds: 14 * 24 * 60 * 60,
    status: AUDIT_SCAN_FREQUENCY_STATUS.NEUTRAL,
    rateValue: 14,
    rateUnit: SCHEDULE_RATE_UNIT.SCHEDULE_RATE_UNIT_DAYS,
  },
  [AuditScanFrequencyOptions.MONTHLY]: {
    label: AuditScanFrequencyLabels.MONTHLY,
    seconds: 28 * 24 * 60 * 60,
    status: AUDIT_SCAN_FREQUENCY_STATUS.NEUTRAL,
    description: 'Worst evidence',
    rateValue: 28,
    rateUnit: SCHEDULE_RATE_UNIT.SCHEDULE_RATE_UNIT_DAYS,
  },
}

export const AUDIT_SCAN_FREQUENCY_SECONDS: Record<
  AuditScanFrequencyOptions,
  number
> = {
  [AuditScanFrequencyOptions.MANUAL_ONLY]:
    AUDIT_SCAN_FREQUENCY_CONFIG_MAP[AuditScanFrequencyOptions.MANUAL_ONLY]
      .seconds,
  [AuditScanFrequencyOptions.EVERY_FOUR_HOURS]:
    AUDIT_SCAN_FREQUENCY_CONFIG_MAP[AuditScanFrequencyOptions.EVERY_FOUR_HOURS]
      .seconds,
  [AuditScanFrequencyOptions.EVERY_TWELVE_HOURS]:
    AUDIT_SCAN_FREQUENCY_CONFIG_MAP[
      AuditScanFrequencyOptions.EVERY_TWELVE_HOURS
    ].seconds,
  [AuditScanFrequencyOptions.DAILY]:
    AUDIT_SCAN_FREQUENCY_CONFIG_MAP[AuditScanFrequencyOptions.DAILY].seconds,
  [AuditScanFrequencyOptions.WEEKLY]:
    AUDIT_SCAN_FREQUENCY_CONFIG_MAP[AuditScanFrequencyOptions.WEEKLY].seconds,
  [AuditScanFrequencyOptions.BIWEEKLY]:
    AUDIT_SCAN_FREQUENCY_CONFIG_MAP[AuditScanFrequencyOptions.BIWEEKLY].seconds,
  [AuditScanFrequencyOptions.MONTHLY]:
    AUDIT_SCAN_FREQUENCY_CONFIG_MAP[AuditScanFrequencyOptions.MONTHLY].seconds,
}

export const getAuditScanFrequencyConfig = (): Record<
  AuditScanFrequencyOptions,
  AuditScanFrequencyConfigType
> => AUDIT_SCAN_FREQUENCY_CONFIG_MAP

export const ACCOUNT_SETTINGS_GRID_TEMPLATE = 'auto 1fr repeat(2, auto)'
